import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NETWORK_ENV } from '../config/chains';

const ACTIVE_TRON_NETWORK = NETWORK_ENV === 'mainnet' ? 'mainnet' : 'shasta';

const TronContext = createContext();

export const useTron = () => {
  const context = useContext(TronContext);
  if (!context) {
    throw new Error('useTron must be used within a TronProvider');
  }
  return context;
};

// Tron network configurations
export const TRON_NETWORKS = {
  mainnet: {
    name: 'Tron Mainnet',
    shortName: 'tron',
    fullHost: 'https://api.trongrid.io',
    blockExplorer: 'https://tronscan.org',
    nativeCurrency: {
      name: 'TRX',
      symbol: 'TRX',
      decimals: 6,
    },
    chainId: 'mainnet',
  },
  shasta: {
    name: 'Tron Shasta Testnet',
    shortName: 'tron-testnet',
    fullHost: 'https://api.shasta.trongrid.io',
    blockExplorer: 'https://shasta.tronscan.org',
    nativeCurrency: {
      name: 'TRX',
      symbol: 'TRX',
      decimals: 6,
    },
    chainId: 'shasta',
  },
};

export const TronProvider = ({ children }) => {
  const [tronWeb, setTronWeb] = useState(null);
  const [account, setAccount] = useState(null);
  const [network, setNetwork] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [waitingForUnlock, setWaitingForUnlock] = useState(false);
  const [tronLinkChecked, setTronLinkChecked] = useState(false); // Track if we've finished checking for TronLink
  const [isTrustHijacking, setIsTrustHijacking] = useState(false);
  const pollIntervalRef = useRef(null);
  const initializingRef = useRef(false); // Prevent duplicate initialization
  const mountedRef = useRef(true);

  // Check if TronLink is installed
  const isTronLinkInstalled = () => {
    // Check for window.tronLink instead of window.tronWeb
    // because tronWeb is only available when unlocked
    return typeof window !== 'undefined' && (
      typeof window.tronLink !== 'undefined' ||
      typeof window.tronWeb !== 'undefined'
    );
  };

  // Trust Wallet's extension injects window.tronLink/tronWeb mimicking
  // TronLink's API, but only points at Tron mainnet. Detect via markers on
  // the *active* tron provider OR by the RPC host — Trust Wallet's TRON
  // support routes through tron.twnodes.com (Trust Wallet Nodes) and doesn't
  // always set the isTrust flag, so flag detection alone misses it.
  const TRUST_HOST_PATTERN = /twnodes\.com|trustwallet/i;

  const collectTronHosts = (tw) => [
    tw?.fullHost,
    tw?.fullNode?.host,
    tw?.solidityNode?.host,
    tw?.eventServer?.host,
    window.tronLink?.tronWeb?.fullHost,
    window.tronLink?.tronWeb?.fullNode?.host,
    window.tronLink?.tronWeb?.solidityNode?.host,
    window.tronWeb?.fullHost,
    window.tronWeb?.fullNode?.host,
  ].filter(Boolean);

  const isTrustTronProvider = () => {
    if (typeof window === 'undefined') return false;
    const tw = window.tronLink?.tronWeb || window.tronWeb;
    if (!tw) return false;
    if (tw.isTrust || tw.isTrustWallet || tw._isTrust) return true;
    return TRUST_HOST_PATTERN.test(collectTronHosts(tw).join(' '));
  };

  const isTronLinkProvider = () => {
    if (typeof window === 'undefined') return false;
    const tl = window.tronLink;
    const tw = tl?.tronWeb || window.tronWeb;
    if (tl?.isTronLink === true) return true;
    if (tw?.isTronLink === true) return true;
    return false;
  };

  // Only flag hijack when Trust is *active* AND TronLink isn't reachable. If
  // both extensions are installed and TronLink identifies itself, TronLink's
  // namespace wins for our connect flow — don't show the user a blocked pill.
  const evaluateHijack = () => isTrustTronProvider() && !isTronLinkProvider();

  const detectTronNetwork = (tronWebInstance) => {
    try {
      const all = collectTronHosts(tronWebInstance).join(' ');
      if (/shasta/i.test(all)) return 'shasta';
      if (/nile/i.test(all)) return 'nile';
      // Trust Wallet's twnodes.com endpoints are mainnet-only.
      if (/api\.trongrid\.io/i.test(all) || TRUST_HOST_PATTERN.test(all)) return 'mainnet';
    } catch {
      // fall through
    }
    return 'mainnet';
  };

  // Initialize TronWeb.  When `silent` is true (used by auto-reconnect on
  // page load) failures are swallowed — the error state is only set when the
  // user explicitly triggers a connection so they get actionable feedback.
  const initializeTronWeb = useCallback(async (silent = false) => {
    if (!isTronLinkInstalled()) {
      return false;
    }

    try {
      // Wait for TronWeb to be injected with a usable default address. The
      // .ready flag isn't reliable — newer TronLink builds and some wallet
      // conflicts leave it false even after the address is set, which is what
      // we actually need to sign anything. Treat .ready as a hint, not a gate.
      let attempts = 0;
      const maxAttempts = 50;
      const getTw = () => window.tronLink?.tronWeb || window.tronWeb;

      const isUsable = (tw) =>
        !!(tw && (tw.ready || tw.defaultAddress?.base58));

      while (!isUsable(getTw())) {
        if (attempts >= maxAttempts) {
          // Diagnostic snapshot — surfaces which provider is actually exposed
          // when the timeout hits, so users on conflicting wallets can see why.
          const tw = getTw();
          console.warn('[Tron] init timed out', {
            hasTronLink: !!window.tronLink,
            tronLinkReady: window.tronLink?.ready,
            hasTronWeb: !!window.tronWeb,
            tronWebReady: window.tronWeb?.ready,
            defaultAddress: tw?.defaultAddress,
            host: tw?.fullHost || tw?.fullNode?.host,
            isTrust: isTrustTronProvider(),
            isTronLink: isTronLinkProvider(),
          });
          throw new Error('TronWeb not ready after maximum attempts');
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      const tronWebInstance = getTw();

      if (!tronWebInstance.defaultAddress?.base58) {
        throw new Error('TronLink wallet not connected');
      }

      const detectedNetwork = detectTronNetwork(tronWebInstance);
      const isTrust = isTrustTronProvider();
      const isTronLink = isTronLinkProvider();

      // Diagnostic log — helps the user see which extension is actually
      // responding when they have multiple Tron-capable wallets installed.
      console.info('[Tron] connect attempt', {
        detectedNetwork,
        expected: ACTIVE_TRON_NETWORK,
        fullHost: tronWebInstance.fullHost,
        fullNode: tronWebInstance.fullNode?.host,
        solidityNode: tronWebInstance.solidityNode?.host,
        eventServer: tronWebInstance.eventServer?.host,
        tronLinkFullHost: window.tronLink?.tronWeb?.fullHost,
        windowTronWebFullHost: window.tronWeb?.fullHost,
        isTrust,
        isTronLink,
      });

      // Reject the wrong network up front instead of letting state fall
      // through to the "Unsupported" warning. Trust Wallet's TRON support
      // hijacks window.tronWeb on Mainnet only — when isTrust is true at all
      // (whether or not TronLink is also present), the connection is going
      // through Trust and the user needs to disable it to reach Shasta.
      if (detectedNetwork !== ACTIVE_TRON_NETWORK) {
        const expectedName = TRON_NETWORKS[ACTIVE_TRON_NETWORK].name;
        const actualName = TRON_NETWORKS[detectedNetwork]?.name || detectedNetwork;
        let msg;
        if (isTrust) {
          msg = `Trust Wallet's TRON support is hijacking this connection (RPC: ${tronWebInstance.fullNode?.host || 'twnodes.com'}). Trust only supports Mainnet, so it blocks TronLink from reaching ${expectedName} even though TronLink is set to it. Fix: open chrome://extensions → disable Trust Wallet (or Trust Wallet → Settings → Preferences → toggle TRON off), then hard-reload this page (Ctrl+Shift+R).`;
        } else {
          msg = `Wallet is on ${actualName}, but this app needs ${expectedName}. Open TronLink → click the network selector at the top of the popup → choose ${expectedName}, then click Connect TronLink again.`;
        }
        throw new Error(msg);
      }

      if (!mountedRef.current) return false;

      setTronWeb(tronWebInstance);
      setAccount(tronWebInstance.defaultAddress.base58);
      setNetwork(TRON_NETWORKS[detectedNetwork]);

      return true;
    } catch (error) {
      // "TronWeb not ready" / "not connected" means the wallet is locked or
      // mid-injection — not user-actionable on its own. The caller falls back
      // to the unlock-polling flow which surfaces a friendly message.
      const transient =
        /not ready|not connected/i.test(error.message || '');
      if (!silent && mountedRef.current && !transient) {
        console.error('Failed to initialize TronWeb:', error);
        setError(error.message);
      }
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if TronLink is unlocked. Mirror initializeTronWeb's "address is
  // sufficient, .ready is a hint" rule so the polling fallback isn't gated on
  // a flag that some TronLink versions never flip.
  const checkIfUnlocked = () => {
    try {
      const tw = window.tronLink?.tronWeb || window.tronWeb;
      return !!(tw && (tw.ready || tw.defaultAddress?.base58));
    } catch (error) {
      return false;
    }
  };

  // Poll for TronLink unlock and auto-connect
  const pollForUnlock = useCallback(() => {
    // Clear any existing polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    let pollCount = 0;
    const maxPolls = 60; // Poll for up to 60 seconds
    const pollInterval = 1000; // Check every second

    pollIntervalRef.current = setInterval(async () => {
      if (!mountedRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        return;
      }

      pollCount++;

      if (checkIfUnlocked()) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (mountedRef.current) setWaitingForUnlock(false);

        try {
          const result = await window.tronLink.request({
            method: 'tron_requestAccounts',
          });

          if (!mountedRef.current) return;

          if (result.code === 200) {
            const success = await initializeTronWeb();
            if (success && mountedRef.current) {
              localStorage.setItem('tronConnected', 'true');
              setError(null);
            }
          }
        } catch (error) {
          // Auto-connect after unlock failed silently
        } finally {
          if (mountedRef.current) setIsConnecting(false);
        }
      } else if (pollCount >= maxPolls) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (mountedRef.current) {
          setWaitingForUnlock(false);
          setIsConnecting(false);
        }
      }
    }, pollInterval);
  }, [initializeTronWeb]);

  // Connect to TronLink
  const connectTronLink = useCallback(async () => {
    if (!isTronLinkInstalled()) {
      const errorMsg = 'TronLink is not installed. Please install TronLink extension.';
      setError(errorMsg);
      alert('TronLink extension not found!\n\nPlease install TronLink from the Chrome Web Store:\nhttps://chrome.google.com/webstore/detail/tronlink');
      return false;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // This call should trigger the TronLink popup (even if locked)
      const result = await window.tronLink.request({
        method: 'tron_requestAccounts',
      });

      if (result.code === 200) {
        // Successfully authorized
        const success = await initializeTronWeb();
        if (success) {
          localStorage.setItem('tronConnected', 'true');
          setIsConnecting(false);
          return true;
        }
        // Code 200 but tronWeb never became ready. TronLink returns 200 for
        // sites it has previously approved even when the wallet is currently
        // locked — fall back to the unlock-polling flow.
        if (!checkIfUnlocked()) {
          setWaitingForUnlock(true);
          setError('Please unlock TronLink and approve the connection');
          pollForUnlock();
          return false;
        }
        // tronWeb is "ready" but init still failed (e.g. wrong network).
        // initializeTronWeb has already set a meaningful error in that case.
        setIsConnecting(false);
        return false;
      } else if (result.code === 4001) {
        // User rejected the request
        setError('Connection request rejected');
        setIsConnecting(false);
        return false;
      } else if (result.code === 4000) {
        // TronLink is locked - start polling for unlock
        setWaitingForUnlock(true);
        setError('Please unlock TronLink and approve the connection');
        pollForUnlock();
        return false;
      } else {
        throw new Error(result.message || 'Failed to connect to TronLink');
      }
    } catch (error) {
      // Check if it's a lock-related error
      const errorMsg = error.message || '';
      if (errorMsg.includes('lock') || errorMsg.includes('password') || !checkIfUnlocked()) {
        setWaitingForUnlock(true);
        setError('Please unlock TronLink extension (click the extension icon and enter your password)');
        pollForUnlock();
        return false;
      }

      setError(errorMsg || 'Failed to connect to TronLink');
      setIsConnecting(false);
      return false;
    }
  }, [initializeTronWeb, pollForUnlock]);

  // Disconnect from TronLink
  const disconnectTronLink = useCallback(() => {
    // Clear any active polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    setTronWeb(null);
    setAccount(null);
    setNetwork(null);
    setError(null);
    setWaitingForUnlock(false);
    setIsConnecting(false);
    localStorage.removeItem('tronConnected');
  }, []);

  // Wait for TronLink to be injected and auto-connect if previously connected.
  // Guarded so React.StrictMode's double-mount and any HMR remount don't fire
  // two parallel polls / two silent reconnect attempts.
  useEffect(() => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    let cancelled = false;

    const waitForTronLink = async () => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max

      while (attempts < maxAttempts) {
        if (cancelled || !mountedRef.current) return;
        if (window.tronLink || window.tronWeb) {
          if (cancelled || !mountedRef.current) return;
          setTronLinkChecked(true);

          const wasConnected = localStorage.getItem('tronConnected');
          if (wasConnected && isTronLinkInstalled()) {
            const ok = await initializeTronWeb(true);
            if (cancelled || !mountedRef.current) return;
            if (ok) {
              // TronLink clearly reachable — never flash the blocked pill.
              setIsTrustHijacking(false);
            } else {
              localStorage.removeItem('tronConnected');
              setIsTrustHijacking(evaluateHijack());
            }
          } else {
            setIsTrustHijacking(evaluateHijack());
          }
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (!cancelled && mountedRef.current) setTronLinkChecked(true);
    };

    waitForTronLink();

    return () => {
      cancelled = true;
      // Leave initializingRef.current = true so StrictMode's remount sees the
      // guard and skips a duplicate run. The provider lives for the app's
      // lifetime — there's no real "remount that should re-init" case.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up event listeners. Only auto-init silently when the user previously
  // connected — surfacing errors here would clobber the WalletConnect UI when
  // TronLink injects late on a page where the user picked a different wallet.
  useEffect(() => {
    if (!isTronLinkInstalled() || !window.tronLink) return;

    const handler = () => {
      // Re-check the hijack flag — Trust may inject after our mount-time scan.
      setIsTrustHijacking(evaluateHijack());
      if (localStorage.getItem('tronConnected') === 'true') {
        initializeTronWeb(true);
      }
    };
    window.addEventListener('tronLink#initialized', handler);
    return () => {
      window.removeEventListener('tronLink#initialized', handler);
    };
    // isTrustTronProvider/isTronLinkInstalled are stable closures over window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeTronWeb]);

  // Mount/unmount guard + cleanup polling interval on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Convert TRX to Sun (smallest unit)
  const toSun = useCallback((trx) => {
    return window.tronWeb?.toSun(trx);
  }, []);

  // Convert Sun to TRX
  const fromSun = useCallback((sun) => {
    return window.tronWeb?.fromSun(sun);
  }, []);

  // Get account balance
  const getBalance = useCallback(async (address = account) => {
    if (!tronWeb || !address) return null;

    try {
      const balance = await tronWeb.trx.getBalance(address);
      return fromSun(balance);
    } catch (error) {
      console.error('Failed to get balance:', error);
      return null;
    }
  }, [tronWeb, account, fromSun]);

  const isNetworkSupported = network && network.chainId === ACTIVE_TRON_NETWORK;

  const value = useMemo(() => ({
    tronWeb,
    account,
    network,
    isConnecting,
    error,
    waitingForUnlock,
    tronLinkChecked,
    isTrustHijacking,
    isTronLinkInstalled,
    isNetworkSupported,
    connectTronLink,
    disconnectTronLink,
    getBalance,
    toSun,
    fromSun,
    supportedNetworks: TRON_NETWORKS,
  }), [
    tronWeb,
    account,
    network,
    isConnecting,
    error,
    waitingForUnlock,
    tronLinkChecked,
    isTrustHijacking,
    isNetworkSupported,
    connectTronLink,
    disconnectTronLink,
    getBalance,
    toSun,
    fromSun,
  ]);

  return <TronContext.Provider value={value}>{children}</TronContext.Provider>;
};